/**
 * @fileoverview Page for creating a new variant under an existing product from an unmatched listing.
 * @module features/sku-alias/pages/CreateProductFromListingPage
 */

import { ReactElement, useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Kbd } from '@/components/ui/kbd'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'

import { ScrollReveal, CostInput, parseCostToNumber } from '@/shared/components/ui'
import { LookupConfirmField } from '@/shared/components/LookupConfirmField'
import { useCreateVariantFromListingMutation } from '@/features/sku-alias/api/needsReview'
import type { UnmatchedListing } from '@/features/sku-alias/schemas/unmatchedListingSchema'
import { getProducts, type Product } from '@/lib/productService'

const createFromListingFormSchema = z.object({
  productId: z.string().uuid('Product is required'),
  sku: z.string().min(1, 'SKU is required'),
  variantName: z.string().optional(),
  imageUrl: z.string().url().optional().or(z.literal('')),
  originalCost: z.string().optional(),
  originalCurrency: z.string().optional(),
})

type CreateFromListingFormValues = z.infer<typeof createFromListingFormSchema>

interface LocationState {
  listing: UnmatchedListing
}

export function CreateProductFromListingPage(): ReactElement {
  const { t } = useTranslation('features-products')
  const { t: tAlias } = useTranslation('features-sku-alias')
  const navigate = useNavigate()
  const location = useLocation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)

  const state = location.state as LocationState | null
  const listing = state?.listing

  // Redirect if no listing data
  useEffect(() => {
    if (!listing) {
      navigate('/dashboard/management/products?view=needsReview', { replace: true })
    }
  }, [listing, navigate])

  const form = useForm<CreateFromListingFormValues>({
    resolver: zodResolver(createFromListingFormSchema),
    defaultValues: {
      productId: '',
      sku: listing?.externalSlug ?? '',
      variantName: listing?.externalSlug ?? '',
      imageUrl: '',
      originalCost: '',
      originalCurrency: '',
    },
  })

  const createMutation = useCreateVariantFromListingMutation()

  const handleSubmit = async (values: CreateFromListingFormValues): Promise<void> => {
    if (!listing) return

    const entryId = listing.entryIds?.[0]
    if (!entryId) {
      toast.error('No entry ID found for this listing')
      return
    }

    setIsSubmitting(true)
    try {
      await createMutation.mutateAsync({
        entryId,
        productId: values.productId,
        sku: values.sku.trim(),
        name: values.variantName?.trim() || values.sku.trim(),
        imageUrl: values.imageUrl?.trim() || null,
        originalCost: values.originalCost ? parseCostToNumber(values.originalCost) : null,
        originalCurrency: values.originalCurrency?.trim() || null,
      })

      toast.success(tAlias('needsReview.success.created'))
      navigate('/dashboard/management/products?view=needsReview')
    } catch (error) {
      const message = error instanceof Error ? error.message : t('create.errors.failed')
      toast.error(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Esc to go back (only when form is not dirty)
  const isDirty = form.formState.isDirty
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && !isDirty) {
        event.preventDefault()
        navigate('/dashboard/management/products?view=needsReview')
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isDirty, navigate])

  if (!listing) {
    return <div />
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-screen-lg mx-auto px-4 lg:px-8 pt-24 pb-8 space-y-8">
        <ScrollReveal blur>
          <div className="flex items-center gap-4 mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard/management/products?view=needsReview')}
              className="gap-2"
            >
              <ArrowLeft className="h-5 w-5" />
              <Kbd>Esc</Kbd>
            </Button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">
                {tAlias('reviewSheet.createSection.title')}
              </h1>
              <p className="text-muted-foreground font-mono text-sm">
                {listing.externalSlug}
              </p>
            </div>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.1}>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)}>
              <section className="flex flex-col rounded-3xl border border-border/15 bg-card px-6 pb-6 pt-6">
                <div className="mb-6">
                  <h3 className="text-lg font-semibold">
                    {t('createPage.sections.productDetails')}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t('createPage.sections.productDetailsDesc')}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="productId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('create.fields.productName')}</FormLabel>
                        <FormControl>
                          <LookupConfirmField<Product>
                            fetchResults={async query => {
                              const response = await getProducts({ query, page: 0, size: 8 })
                              return response.content ?? []
                            }}
                            onConfirm={product => {
                              setSelectedProduct(product)
                              field.onChange(product.id)
                            }}
                            renderSuggestion={({ item }) => (
                              <div className="flex flex-col gap-0.5">
                                <span className="font-medium">{item.name}</span>
                                {item.brand ? (
                                  <span className="text-xs text-muted-foreground">{item.brand}</span>
                                ) : null}
                              </div>
                            )}
                            getItemLabel={item => item.name}
                            placeholder={t('create.placeholders.productName')}
                            minSearchLength={1}
                            maxVisibleResults={6}
                            queryKeyBase={['product', 'search']}
                            onCreateNew={() => navigate('/dashboard/management/products/new')}
                            createNewLabel="Create product"
                            initialSelectedItem={selectedProduct ?? undefined}
                            clearOnBlur={false}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="sku"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('create.fields.skuSlug')}</FormLabel>
                        <FormControl>
                          <Input
                            placeholder={t('create.placeholders.skuSlug')}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="variantName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('create.fields.variantName')}</FormLabel>
                          <FormControl>
                            <Input
                              placeholder={t('create.placeholders.variantName')}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <CostInput
                      label={t('create.fields.originalCost')}
                      costValue={form.watch('originalCost') ?? ''}
                      onCostChange={(val) => form.setValue('originalCost', val, { shouldDirty: true })}
                      currencyValue={form.watch('originalCurrency') ?? undefined}
                      onCurrencyChange={(val) => form.setValue('originalCurrency', val, { shouldDirty: true })}
                    />

                    <FormField
                      control={form.control}
                      name="imageUrl"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('create.fields.imageUrl')}</FormLabel>
                          <FormControl>
                            <Input
                              placeholder={t('create.placeholders.imageUrl')}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-border/15">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/dashboard/management/products?view=needsReview')}
                    disabled={isSubmitting}
                  >
                    {t('create.actions.cancel')}
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('create.actions.saving')}
                      </>
                    ) : (
                      t('create.actions.createAndLink')
                    )}
                  </Button>
                </div>
              </section>
            </form>
          </Form>
        </ScrollReveal>
      </div>
    </div>
  )
}

export default CreateProductFromListingPage
